import { useEffect, useState } from "react";
import { useGetList, useNotify, useRefresh, useUpdate } from "ra-core";
import { Button } from "@/components/ui/button";

import { SectionCard } from "../data-display/SectionCard";
import type { Sequence } from "../sequences/sequenceModel";
import { formsInputClass } from "./FormsPage";
import type { FormSettings, LeadForm } from "./formModel";

/**
 * What happens after a submission: success message, deal creation,
 * follow-up task, sequence enrollment. All executed server-side by
 * submit_form, this panel only edits the settings jsonb.
 */
export const FormSettingsPanel = ({ form }: { form: LeadForm }) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [update, { isPending }] = useUpdate();
  const [settings, setSettings] = useState<FormSettings>(form.settings);

  useEffect(() => setSettings(form.settings), [form.id, form.settings]);

  const { data: sequences } = useGetList<Sequence>("sequences", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "name", order: "ASC" },
  });

  const patch = (changes: Partial<FormSettings>) =>
    setSettings((prev) => ({ ...prev, ...changes }));

  const save = () =>
    update(
      "forms",
      { id: form.id, data: { settings }, previousData: form },
      {
        mutationMode: "pessimistic",
        onSuccess: () => {
          notify("Settings saved");
          refresh();
        },
        onError: (error: unknown) =>
          notify(error instanceof Error ? error.message : "Could not save", {
            type: "error",
          }),
      },
    );

  return (
    <SectionCard
      title="After submission"
      subtitle="Executed server-side on every submission"
    >
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-2 rounded-md border border-dashed border-border p-3">
          <span className="text-[12.5px] font-medium">
            Landing page content
          </span>
          <span className="text-[11.5px] text-muted-foreground">
            The public form page doubles as a landing page; this fills its hero
            panel. Blank fields fall back to the standard copy.
          </span>
          <label className="flex flex-col gap-1 text-[12.5px] font-medium">
            Headline
            <input
              className="h-8 w-full rounded-md border border-input bg-transparent px-2.5 text-[13px] outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={settings.landing?.headline ?? ""}
              placeholder="Fall tune-up special"
              onChange={(e) =>
                patch({
                  landing: { ...settings.landing, headline: e.target.value },
                })
              }
            />
          </label>
          <label className="flex flex-col gap-1 text-[12.5px] font-medium">
            Supporting line
            <input
              className="h-8 w-full rounded-md border border-input bg-transparent px-2.5 text-[13px] outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={settings.landing?.subtext ?? ""}
              placeholder="Book before Friday and save 20%"
              onChange={(e) =>
                patch({
                  landing: { ...settings.landing, subtext: e.target.value },
                })
              }
            />
          </label>
          <label className="flex flex-col gap-1 text-[12.5px] font-medium">
            Selling points (one per line)
            <textarea
              className="min-h-16 w-full rounded-md border border-input bg-transparent px-2.5 py-1.5 text-[13px] outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={(settings.landing?.bullets ?? []).join("\n")}
              placeholder={"Licensed and insured\nSame-week scheduling"}
              onChange={(e) =>
                patch({
                  landing: {
                    ...settings.landing,
                    // Keep empty lines while typing (filtering them here
                    // would make Enter impossible in a controlled field);
                    // the public page skips blanks at render time.
                    bullets: e.target.value.split("\n"),
                  },
                })
              }
            />
          </label>
        </div>

        <label className="flex flex-col gap-1 text-[12.5px] font-medium">
          Success message
          <textarea
            className="min-h-16 w-full rounded-md border border-input bg-transparent px-2.5 py-1.5 text-[13px] outline-none focus-visible:ring-2 focus-visible:ring-ring"
            value={settings.success_message ?? ""}
            placeholder="Thanks! We received your request…"
            onChange={(e) => patch({ success_message: e.target.value })}
          />
        </label>

        <label className="flex items-center gap-2 text-[13px]">
          <input
            type="checkbox"
            className="size-4 accent-primary"
            checked={settings.create_task ?? true}
            onChange={(e) => patch({ create_task: e.target.checked })}
          />
          Create a follow-up task for each new lead
        </label>

        <label className="flex items-center gap-2 text-[13px]">
          <input
            type="checkbox"
            className="size-4 accent-primary"
            checked={settings.create_deal ?? false}
            onChange={(e) => patch({ create_deal: e.target.checked })}
          />
          Create a deal in the pipeline
        </label>

        <label className="flex flex-col gap-1 text-[12.5px] font-medium">
          Enroll in follow-up sequence
          <select
            className={formsInputClass}
            value={settings.sequence_id ?? ""}
            onChange={(e) => patch({ sequence_id: e.target.value || null })}
          >
            <option value="">No sequence</option>
            {(sequences ?? []).map((sequence) => (
              <option key={sequence.id} value={sequence.id}>
                {sequence.name}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-[12.5px] font-medium">
          Default source (when no UTM present)
          <input
            className={formsInputClass}
            value={settings.default_source ?? ""}
            placeholder="web_form"
            onChange={(e) =>
              patch({ default_source: e.target.value || undefined })
            }
          />
        </label>

        <label className="flex flex-col gap-1 text-[12.5px] font-medium">
          Redirect URL after submit (optional)
          <input
            className={formsInputClass}
            value={settings.redirect_url ?? ""}
            placeholder="https://…"
            onChange={(e) => patch({ redirect_url: e.target.value || null })}
          />
        </label>

        <div className="flex justify-end">
          <Button size="sm" disabled={isPending} onClick={save}>
            Save settings
          </Button>
        </div>
      </div>
    </SectionCard>
  );
};
